import { css, cx, keyframes, injectGlobal } from 'emotion';
import './sanitize.css';
import './museo-sans.css';
import './helvetica-neue.css';

import headingIcon from 'inside-out/images/resource-library/heading-icon.svg';
import factboxIcon from 'inside-out/images/resource-library/factbox-icon.svg';
import blockquoteIcon from 'inside-out/images/resource-library/blockquote-icon.svg';

// ----------------------------------------------------
// Globals
// ----------------------------------------------------

injectGlobal`
	* {
		box-sizing: border-box;
	}
	html {
		font-family: 'HelveticaNeue', Helvetica, Arial, 'Lucida Grande', sans-serif;
		font-weight: 300;
	}
	body {
		margin: 0;
	}
	html.noScroll, html.noScroll body {
		height: 100%;
		overflow: hidden;
	}
`;

// ----------------------------------------------------
// Fonts
// ----------------------------------------------------

export const museo500 = {
	fontFamily: 'MuseoSansRounded-500',
	letterSpacing: '0.5px',
};

export const museo900 = {
	fontFamily: 'MuseoSansRounded-900',
	letterSpacing: '0.5px',
};

// ----------------------------------------------------
// Colours
// ----------------------------------------------------

export const lightGrey = '#EBEBEC';
export const grey = '#8A8B8C';
export const darkGrey = '#242424';
export const darkBlueGrey = '#505E64';
export const pink = '#F05B5E';
export const aqua = '#6AC8C8';
export const purple = '#714B9F';
export const orange = '#F5A81E';
export const burnedOrange = '#F26C22';
export const blue = '#4598CB';
export const yellow = '#F8DA3D';

export const audienceStylesHash = {
	'family-and-carers': orange,
	'for-myself': burnedOrange,
	'for-healthcare-professionals': blue,
	'': grey,
	'me': burnedOrange,
	'carers': orange,
	'professionals': blue,
	'service_development': pink,
	'researchers': aqua,
};

// ----------------------------------------------------
// Breakpoints
// ----------------------------------------------------

// Breakpoint + 40px padding
export const desktopMaxWidthBreakpoint = 1210;
export const desktopMinWidthBreakpoint = 1170;
export const tabletLandscapeMaxWidthBreakpoint = 1064;
export const tabletLandscapeMinWidthBreakpoint = 1024;
export const tabletPortraitMaxWidthBreakpoint = 808;
export const tabletPortraitMinWidthBreakpoint = 768;
export const mobileMaxWidthBreakpoint = 520;
export const mobileMinWidthBreakpoint = 320;

// export const mediaQuery = (() => {
// 	const breakpoints = {
// 		desktopMaxWidthBreakpoint: 1210,
// 		desktopMinWidthBreakpoint: 1170,
// 		tabletLandscapeMaxWidthBreakpoint: 1064,
// 		tabletLandscapeMinWidthBreakpoint: 1024,
// 		tabletPortraitMaxWidthBreakpoint: 808,
// 		tabletPortraitMinWidthBreakpoint: 768,
// 		mobileMaxWidthBreakpoint: 520,
// 		mobileMinWidthBreakpoint: 320,
// 	};

// 	return {
// 		breakpoints: breakpoints,
// 		query: {
// 			min: Object.assign(
// 				...Object.keys(breakpoints)

// 					.sort()
// 					.map(c => {
// 						return {
// 							[breakpoints[c]]: `@media (min-width: ${breakpoints[c]}px)`,
// 						};
// 					})
// 			),
// 			max: Object.assign(
// 				...Object.keys(breakpoints)

// 					.sort()
// 					.map(c => {
// 						return {
// 							[breakpoints[c]]: `@media (max-width: ${breakpoints[c]}px)`,
// 						};
// 					})
// 			),
// 		},
// 	};
// })();

// ----------------------------------------------------
// Icons
// ----------------------------------------------------

import CallIcon from 'inside-out/images/support-organisations/call-icon.svg';
import FaxIcon from 'inside-out/images/support-organisations/fax-icon.svg';
import LinkIcon from 'inside-out/images/support-organisations/link-icon.svg';
import TimeIcon from 'inside-out/images/support-organisations/time-icon.svg';
import EmailIcon from 'inside-out/images/support-organisations/email-icon.svg';
import LocationIcon from 'inside-out/images/support-organisations/location-icon.svg';
import CalendarIcon from 'inside-out/images/support-organisations/calendar-icon.svg';
import DropDownIcon from 'inside-out/images/dropdown-icon.svg';
import PasswordMaskIcon from 'inside-out/images/password-mask-icon.svg';
import CheckedIcon from 'inside-out/images/checked-icon.svg';

// ----------------------------------------------------
// Headings
// ----------------------------------------------------

export const headingXLarge = css({
	...museo900,
	color: darkGrey,
	fontSize: '48px',
	lineHeight: '57px',
	fontWeight: 'normal',
	margin: '0',
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		fontSize: '32px',
		lineHeight: '38px',
	},
});

export const headingLarge = css({
	...museo900,
	color: darkGrey,
	fontSize: '36px',
	lineHeight: '43px',
	fontWeight: 'normal',
	margin: '0',
	[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		fontSize: '26px',
		lineHeight: '30px',
	},
});

export const headingMedium = css({
	...museo900,
	color: darkGrey,
	fontSize: '22px',
	lineHeight: '27px',
	fontWeight: 'normal',
	margin: '0',
});

export const headingMediumLight = cx(
	headingMedium,
	css({
		...museo500,
	})
);

export const headingSmall = css({
	...museo900,
	color: darkGrey,
	fontSize: '18px',
	lineHeight: '22px',
	fontWeight: 'normal',
	margin: '0',
});

export const headingSmallLight = cx(
	headingSmall,
	css({
		...museo500,
	})
);

// ----------------------------------------------------
// Text
// ----------------------------------------------------

export const textBlock = css({
	color: grey,
	fontSize: '16px',
	lineHeight: '22px',
});

export const textBlockLarger = css({
	color: grey,
	fontSize: '18px',
	lineHeight: '26px',
});

export const textBlockRichFormat = cx(
	textBlockLarger,
	css({
		maxWidth: '760px',
		color: darkGrey,
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			fontSize: '14px',
		},

		'p:first-child': {
			marginTop: 0,
		},

		h2: {
			...museo900,
			color: pink,
			fontSize: '24px',
			lineHeight: '28px',
			margin: '0 0 17px 0',
			position: 'relative',

			'&:before': {
				content: '""',
				position: 'absolute',
				width: '50px',
				height: '35px',
				left: '-58px',
				top: '50%',
				transform: 'translateY(-50%)',
				background: `no-repeat url(${headingIcon}) center center`
			}
		},

		a: {
			color: aqua,
		},

		img: {
			maxWidth: '100%',
		},

		'.highlight-section': {
			margin: '32px 0',
			padding: '60px 24px 24px',
			position: 'relative',
			borderRadius: '4px',
			fontWeight: '600',
			backgroundColor: lightGrey,
			color: darkBlueGrey,

			'& p': {
				margin: '0',
			},

			'&:before': {
				content: '""',
				position: 'absolute',
				width: '36px',
				height: '36px',
				left: '24px',
				top: '12px',
				backgroundRepeat: 'no-repeat',
				backgroundPosition: 'center center',
			},

			'&.factbox': {
				color: '#fff',
				backgroundColor: purple,

				'&:before': {
					backgroundImage: `url(${factboxIcon})`,
				},

				'&:after': {
					content: '"Fact"',
					position: 'absolute',
					top: '23px',
					left: '68px',
					fontSize: '12px',
					lineHeight: '15px',
					fontWeight: '900',
					textTransform: 'uppercase',
				},
			},

			'&.quote:before': {
				backgroundImage: `url(${blockquoteIcon})`,
			},
		},

		'.mediacaption': {
			marginTop: '-1rem',
			marginBottom: '32px',
			fontSize: '14px',
			lineHeight: '26px',
			color: grey,
		},
	})
);

export const label = css({
	fontSize: '12px',
	lineHeight: '15px',
	//color: '#8A8B8C',
	color: grey,
	fontWeight: 500,
	letterSpacing: '0.5px',
	display: 'block',
	marginBottom: '8px',
	textTransform: 'uppercase',
});

// ----------------------------------------------------
// Buttons
// ----------------------------------------------------

export const button = color =>
	css({
		...museo500,
		background: color,
		borderRadius: '4px',
		color: '#fff',
		padding: '15px 19px 13px 19px',
		fontSize: '15px',
		lineHeight: '15px',
		letterSpacing: '0.75px',
		textTransform: 'uppercase',
		border: 'none',
		display: 'inline-block',
		textDecoration: 'none',
	});

const progressBarStripes = keyframes`
	to { background-position: 40px 0 }
  from { background-position: 0 0 }
`;

// ----------------------------------------------------
// Form
// ----------------------------------------------------

export const formSection = (() => {
	const base = {
		wrapper: css({
			width: '100%',
			maxWidth: '500px',
		}),
		fieldset: css({
			border: 'none',
			padding: '0',
			marginTop: '60px',
			'&:last-child': {
				marginBottom: '60px',
			},
			'& legend': cx(
				headingSmallLight,
				css({
					lineHeight: '26px',
					borderBottom: `1px solid ${lightGrey}`,
					paddingBottom: '25px',
					marginBottom: '25px',
					width: '100%',
				})
			),
		}),
		field: css({
			marginBottom: '18px',
			'& label': label,
			'& > div': {
				position: 'relative',
			},
			'& select': {
				border: `1px solid ${lightGrey}`,
				color: darkGrey,
				borderRadius: '4px',
				fontSize: '16px',
				lineHeight: '18px',
				padding: '14px 18px',
				width: '100%',
				appearance: 'none',
				cursor: 'pointer',
				background: `#F6F6F6 url(${DropDownIcon}) no-repeat calc(100% - 16px) center`,
			},
			'& textarea': {
				height: '184px',
			},
			'& input, & textarea': {
				background: '#F6F6F6',
				border: `1px solid ${lightGrey}`,
				color: darkGrey,
				borderRadius: '4px',
				fontSize: '16px',
				lineHeight: '18px',
				padding: '14px 18px',
				width: '100%',
				'&:focus': {
					background: '#fff',
				},
				// Password Mask
				'& + a': {
					position: 'absolute',
					top: '50%',
					transform: 'translateY(-50%)',
					textIndent: '-99999px',
					right: '16px',
					width: '22px',
					height: '16px',
					background: `url(${PasswordMaskIcon}) no-repeat center center`,
				},
			},
		}),
		dropZoneField: {
			default: css({
				position: 'relative',
				maxWidth: '500px',
				height: '140px',
				border: `2px dashed ${grey}`,
				background: '#f6f6f6',
				borderRadius: '4px',
				'& p': {
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					textAlign: 'center',
					color: grey,
					margin: 0,

					'& span:last-child': {
						marginTop: '12px',
					},
				},
			}),
		},
		checkbox: css({
			textIndent: '-99999px',
			fontSize: 0,
			width: '16px',
			height: '16px',
			display: 'inline-block',
			position: 'relative',
			cursor: 'pointer',
			verticalAlign: 'middle',
			'&:before': {
				content: "' '",
				display: 'block',
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
				borderRadius: '3px',
				background: '#fff',
				border: `1px solid ${lightGrey}`,
			},
			'&:checked:after': {
				content: "' '",
				display: 'block',
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
				backgroundColor: pink,
				backgroundImage: `url(${CheckedIcon})`,
				backgroundPosition: 'center center',
				backgroundRepeat: 'no-repeat',
				backgroundSize: '10px',
				borderRadius: '3px',
			},
		}),
		halfWidthWrapper: css({
			[`@media (min-width: ${tabletPortraitMinWidthBreakpoint}px)`]: {
				display: 'flex',
				alignItems: 'flex-end',
				width: '100%',
				'& > div': {
					width: '50%',
					'&:first-child': {
						paddingRight: '8px',
					},
					'&:last-child': {
						paddingLeft: '8px',
					},
				},
			},
		}),
		submitButton: button(pink),
		progressiveButton: loading => cx(
			button(pink),
			loading
			? css({
				backgroundRepeat: 'repeat-x',
				backgroundImage: `linear-gradient(-45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)`,
				backgroundSize: '40px 100%',
				animation: `${progressBarStripes} 0.5s linear infinite`,
			})
			: null
		),
		validationSummary: css({
			position: 'absolute',
			left: '-99999px',
		}),
		validationFieldMessage: css({
			color: 'red',
			fontSize: '10px',
			lineHeight: '12px',
			letterSpacing: '0.5px',
			fontWeight: 'bold',
			marginBottom: '8px',
			'& ~ input, & ~ div input, & ~ textarea, & ~ select': {
				borderColor: 'red',
			},
			'& + label': {
				color: 'red',
			},
		}),
		validationMessage: css({
			color: 'red',
			marginBottom: '18px',
		}),
	};

	return {
		...base,
		phoneField: cx(
			base.field,
			css({
				'& select': css({
					width: '90px',
					borderTopRightRadius: '0',
					borderBottomRightRadius: '0',
				}),
				'& input': css({
					width: 'calc(100% - 90px)',
					borderLeft: 'none',
					borderTopLeftRadius: '0',
					borderBottomLeftRadius: '0',
				}),
				[`& .${base.validationFieldMessage} ~ select, & .${base.validationFieldMessage} ~ input`]: {
					borderColor: 'red',
				},
			})
		),
		suffixField: cx(
			base.field,
			css({
				paddingRight: '46px',
				'& input': css({
					width: 'calc(100% - 46px)',
					'& + span': {
						marginLeft: '8px',
						fontSize: '16px',
						lineHeight: '26px',
						color: grey,
					},
				}),
				// Suffix
			})
		),
	};
})();

// ----------------------------------------------------
// Shared
// ----------------------------------------------------

export const contentSection = (background = '#F6F6F6', padding = '60px') =>
	css({
		paddingTop: padding,
		paddingBottom: padding,
		backgroundColor: background,
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			paddingTop: `${parseInt(padding) / 3}px`,
			paddingBottom: `${parseInt(padding) / 3}px`,
		},
	});

export const cardSection = (margin = 0, arrowTo = false) =>
	css({
		margin: `${margin}px 0`,
		...(arrowTo
			? {
					[`@media (min-width: ${tabletPortraitMaxWidthBreakpoint + 1}px)`]: {
						paddingRight: '14px',
					},
			  }
			: null),
		'& header': {
			marginBottom: '25px',
			position: 'relative',
			'& a': {
				position: 'absolute',
				top: '50%',
				right: '0',
				transform: 'translateY(-50%)',
				color: grey,
				textDecoration: 'none',
				fontSize: '16px',
				'& svg': {
					marginLeft: '4px',
				},
				[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
					fontSize: '12px',
					lineHeight: '24px',
					'& svg': {
						display: 'none',
					},
				},
			},
		},
		[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
			marginRight: '-20px',
			'& [data-component=Cards]': {
				paddingRight: '20px',
				'&[data-scrollable=true] > div': {
					paddingRight: '20px',
				},
			},
			'& header': {
				marginRight: '20px',
			},
		},
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			marginTop: margin ? `${margin / 2}px` : '15px',
			marginBottom: margin ? `${margin / 2}px` : '15px',
		},
	});

export const contactList = {
	wrapper: css({
		margin: '0',
		padding: '0',
		maxWidth: '630px',
		'& li': {
			listStyle: 'none',
			display: 'inline-block',
			minWidth: '315px',
			'&:not(:last-child)': {
				marginBottom: '25px',
			},
			[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
				minWidth: 'auto',
				width: '100%',
			},
		},
		'& li.fullWidth': {
			minWidth: 'auto',
			width: '100%'
		},
		'& li a': {
			textDecoration: 'none',
		},
		// In case of stretch item
		'&:after': {
			content: '" "',
			display: 'block',
			clear: 'both',
		},
	}),
	item: (type, stretch) => {
		let typeImage;
		switch (type) {
			case 'phone-number':
				typeImage = CallIcon;
				break;
			case 'fax-number':
				typeImage = FaxIcon;
				break;
			case 'link':
				typeImage = LinkIcon;
				break;
			case 'opening-hours':
				typeImage = TimeIcon;
				break;
			case 'email':
				typeImage = EmailIcon;
				break;
			case 'location':
				typeImage = LocationIcon;
				break;
			case 'date':
				typeImage = CalendarIcon;
				break;
		}

		return css({
			...museo500,
			fontSize: '16px',
			lineHeight: '22px', // 19px
			paddingLeft: '33px',
			position: 'relative',
			color: darkGrey,
			'& a': {
				color: 'inherit',
			},
			...(stretch
				? {
						float: 'left',
						width: '800px',
				  }
				: null),
			'&:after': {
				position: 'absolute',
				top: '0',
				left: '0',
				display: 'block',
				width: '22px',
				height: '22px',
				content: '" "',
				backgroundRepeat: 'no-repeat',
				backgroundImage: `url(${typeImage})`,
			},
		});
	},
};

export const sideBarLayout = {
	wrapper: css({
		maxWidth: desktopMaxWidthBreakpoint,
		width: '100%',
		height: '100%',
		margin: '0 auto',
		padding: '0 20px',
		overflowX: 'hidden',
		'&:after': {
			content: "' '",
			clear: 'both',
			display: 'block',
		},
		[`@media (max-width: ${desktopMaxWidthBreakpoint}px)`]: {
			// Content
			'& > div:first-child': {
				float: 'none',
				width: '100%',
			},
			// Sidebar
			'& > div:last-child:not(:first-child)': {
				display: 'none',
			},
		},
	}),
	leftSide: margin =>
		css({
			margin: `${margin}px 0`,
			float: 'left',
			width: 'calc(100% - 288px)' /* Right side and spacing */,
		}),
	rightSide: margin =>
		css({
			float: 'right',
			width: '230px',
			'& > *:first-child': {
				marginTop: margin + 12,
			},
			'& > *:last-child': {
				marginBottom: margin + 12,
			},
			'& aside': {
				'& > h1, & > div > h1': {
					...museo900,
					marginTop: 0, // +10 for card margin
					marginBottom: '16px',
					color: darkGrey,
					fontSize: '12px',
					lineHeight: '14px',
					fontWeight: 'normal',
					textTransform: 'uppercase',
				},
			},
		}),
	mobileFilters: css({
		display: 'none',

		[`@media (max-width: ${desktopMaxWidthBreakpoint}px)`]: {
			display: 'block',
			padding: '20px 10px',
			borderBottom: `1px solid ${lightGrey}`,
			borderTop: `1px solid ${lightGrey}`,
			'& [data-component=FilterDropDown]': {
				display: 'inline-block',
				marginLeft: '10px',
			},
		},
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			padding: '7px 0',
		},
	}),
	mobileFiltersDropDown: css({
		position: 'relative',
		boxShadow: 'rgba(0,0,0,0.09) 0 6px 12px 0',
		background: '#fff',
		zIndex: '3',
		padding: '20px',
	}),
};

export const detailSideBarLayout = {
	wrapper: css({
		display: 'table',
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			display: 'block',
		},
	}),
	leftSide: css({
		padding: '0 37px',
		display: 'table-cell',
		verticalAlign: 'top',
		width: '760px',
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			display: 'block',
			width: '100%',
			padding: '0 10px',
		},
	}),
	rightSide: css({
		display: 'table-cell',
		padding: '0 20px',
		verticalAlign: 'top',
		[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
			display: 'none',
		},
	}),
};

export const metaTable = css({
	'& th, & td': {
		verticalAlign: 'top',
	},

	'& th span': cx(
		label,
		css({
			textAlign: 'left',
			fontSize: '10px',
			lineHeight: '12px',
		})
	),

	'& td span': {
		...museo500,
		display: 'block',
		fontSize: '16px',
		lineHeight: '19px',
		color: darkGrey,
	},

	[`@media (min-width: ${tabletPortraitMaxWidthBreakpoint + 1}px)`]: {
		'& th:not(:last-child) span': {
			marginRight: '60px',
		},
		'& td:not(:last-child) span': {
			marginRight: '60px',
		},
		'& table:last-child': {
			display: 'none',
		},
	},

	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		'& table:first-child': {
			display: 'none',
		},
		'& table:last-child': {
			display: 'table',
			'& tr:not(:last-child) th span': {
				marginBottom: '30px',
				marginRight: '30px',
			},
			'& tr::not(:last-child) td span': {
				marginBottom: '30px',
				marginRight: '30px',
			},
		},
	},

	/*[`@media (max-width: ${mobileMaxWidthBreakpoint}px)`]: {
		'& tbody': {
			display: 'flex',

			'& tr': {
				display: 'flex',
				flexDirection: 'column',
				'& th, & td': {
					height: '50px',
				},
			},
		},
	},*/
});

export const quadrantsStyle = css({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	alignItems: 'stretch',
	alignContent: 'stretch',
	position: 'relative',
	overflowX: 'hidden',
	'& > section': {
		width: '50%',
		maxWidth: '458px',
		padding: '57px 28px',
		'& img': {
			display: 'block',
			marginBottom: '20px',
		},
		'& h1': cx(
			headingLarge,
			css({
				marginBottom: '26px',
			})
		),
		'& p': cx(
			textBlock,
			css({
				marginTop: '0',
				marginBottom: '40px',
			})
		),
		'&:nth-child(2n+3)': {
			position: 'relative',
			'&:after': {
				position: 'absolute',
				top: '0',
				left: '0',
				width: '3000px',
				content: "' '",
				height: '1px',
				borderBottom: `1px solid ${lightGrey}`,
			},
		},
	},

	[`@media (max-width: ${tabletPortraitMaxWidthBreakpoint}px)`]: {
		display: 'block',
		'& > section': {
			padding: '28px',
			width: '100%',
			maxWidth: 'initial',
		},
		'&:after': {
			display: 'none',
		},
	},
});



// WEBPACK FOOTER //
// ./src/global-styles/index.js